import React, { useContext, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Text, Modal } from "react_reusable_components";

import CustomSelect from "../../components/CustomSelect/CustomSelect";
import { ThreeRow, TwoColumn, TwoRow } from "../../core/layouts";
import QuestionnaireService from "../../services/questionnaire-service";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import { getLocalizedMessage } from "../../UtilityFunctions/utils";
import variables from "../../themes/base/styles/variables";
import Styles from "./ViewCountry.module.scss";

const modalStyles = {
  assignedSubProductHeading: {
    fontSize: variables.fontSizeXlarge,
    lineHeight: variables.lineHeightXxlarge,
    fontWeight: variables.fontWeightBold,
    color: variables.textDefault,
  },
  modalMaskStyle: {
    backdropFilter: variables.highBlur,
    background: variables.blurBackground,
  },
  cross: {
    height: variables.iconHeightLarge,
    width: variables.iconWidthLarge,
    cursor: "pointer",
  },
  forwardArrow: {
    height: variables.iconHeight,
    width: variables.iconWidth,
    cursor: "pointer",
  },
  middleSectionStyle: {
    marginBottom: variables.marginMedium,
    padding: `${variables.paddingMedium} ${variables.paddingMedium} ${variables.paddingNone}`,
  },
  leftSectionStyle: {
    gap: variables.gapXS,
  },
  bottomSectionStyle: {
    padding: variables.paddingMedium,
  },
  questionnaireCountryNameStyle: {
    fontSize: variables.fontSizeNormal,
    lineHeight: variables.lineHeightSmall,
    fontWeight: variables.fontWeightBold,
    color: variables.textDefault,
  },
  viewQuestionnaireTextStyle: {
    fontSize: variables.fontSizeSmall,
    lineHeight: variables.lineHeightNormal,
    fontWeight: variables.fontWeightNormal,
    color: variables.primaryBg,
  },
  baseFormEditedTextStyle: {
    fontSize: variables.fontSizeSmallest,
    lineHeight: variables.lineHeightMedium,
    fontWeight: variables.fontWeightNormal,
    color: variables.grey2,
  },
  selectCountryStyle: {
    fontSize: variables.fontSizeNormal,
    lineHeight: variables.lineHeightSmall,
    fontWeight: variables.fontWeightBold,
    color: variables.textDefault,
  },
  chooseCountryDescriptionStyle: {
    fontSize: variables.fontSizeSmall,
    lineHeight: variables.lineHeightNormal,
    fontWeight: variables.fontWeightNormal,
  },
  bottomSectionContainerStyle: {
    gap: variables.gapSmaller,
  },
  baseFormEditedWrapperStyle: {
    padding: `${variables.paddingSmallest} ${variables.paddingSmall}`,
    border: variables.popoverBorder,
  },
  baseFormEditedContainerStyle: {
    display: variables.dFlex,
    alignItems: variables.alignCenter,
    justifyContent: variables.spaceBetween,
  },
  topSectionWrapperStyle: {
    display: variables.dFlex,
    alignItems: variables.alignCenter,
    justifyContent: variables.spaceBetween,
    borderBottom: variables.popoverBorder,
    padding: `${variables.paddingMedium} ${variables.paddingMedium} ${variables.paddingSmall}`,
  },
  rightSectionBaseFormStyle: {
    display: variables.dFlex,
    alignItems: variables.alignCenter,
    gap: variables.gapSmaller,
    height: variables.fullHeight,
  },
  selectCountryWrapperStyle: {
    marginTop: variables.marginMediumSmall,
    maxWidth: "18.75rem",
  },
};

export default function ViewCountryWiseQuestionnaireModal({
  open,
  countryOptions,
  onClose,
  activeRecord,
}) {
  const intl = useIntl();
  const [countrySelected, setCountrySelected] = useState(false);
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const navigate = useNavigate();

  const getCountryNameById = (countryId) => {
    const currentCountry = countryOptions?.find(
      (country) => country?.id === countryId
    );
    return currentCountry?.name;
  };

  const getQuestionnaireEditedByCountryId = (countryId) => {
    const currentCountry = countryOptions?.find(
      (country) => country?.id === countryId
    );
    return currentCountry?.isQuestionnaireEdited;
  };

  const getQuestionnaireCodeHandler = (countryId) => {
    if (getQuestionnaireEditedByCountryId(countryId)) {
      QuestionnaireService.getQuestionnaireCodeForSelectedCountry({
        loaderDispatch,
        productSubCategoryId: activeRecord?.productSubCategory?.id,
        countryId,
        intl,
      }).then((res) => {
        let questionnaireVersionCode = res?.data?.data?.questionnaireCode;
        navigate(`/view-questionnaire/${questionnaireVersionCode}`);
      });
    } else {
      navigate(`/view-questionnaire/${activeRecord?.questionnaireCode}`);
    }
  };

  return (
    <Modal
      maskClosable={false}
      maskStyle={modalStyles.modalMaskStyle}
      centered
      open={open}
      closable={false}
      footer={null}
      disableGutter
    >
      <ThreeRow
        style={{ width: variables.fullWidth }}
        topSection={
          <TwoColumn
            style={modalStyles.topSectionWrapperStyle}
            leftSectionStyle={modalStyles.leftSectionStyle}
            leftSection={
              <Text style={modalStyles.assignedSubProductHeading}>
                {getLocalizedMessage(
                  intl,
                  "modal.countryWise.viewQuestionnaire"
                )}
              </Text>
            }
            rightSection={
              <img
                style={modalStyles.cross}
                src={"/icons/close.svg"}
                alt="close"
                onClick={onClose}
              />
            }
          />
        }
        middleSectionStyle={modalStyles.middleSectionStyle}
        middleSection={
          <ThreeRow
            style={{ overflow: "unset" }}
            topSection={
              <Text style={modalStyles.selectCountryStyle}>
                {getLocalizedMessage(intl, "productCategory.selectCountry")}
              </Text>
            }
            middleSectionStyle={{ marginTop: variables.marginHalf }}
            middleSection={
              <Text style={modalStyles.chooseCountryDescriptionStyle}>
                {activeRecord?.isRepair
                  ? getLocalizedMessage(
                      intl,
                      "modal.description.chooseCountry",
                      {
                        subProductName: activeRecord?.productSubCategory?.name,
                      }
                    )
                  : getLocalizedMessage(
                      intl,
                      "modal.description.chooseCountryForOperational"
                    )}
              </Text>
            }
            bottomSectionStyle={modalStyles.selectCountryWrapperStyle}
            bottomSection={
              <CustomSelect
                placeholder={getLocalizedMessage(
                  intl,
                  "addUser.selectUserCountry.placeholder"
                )}
                options={countryOptions?.map((country) => {
                  return {
                    label: (
                      <Text className={`${Styles["select-item-style"]}`}>
                        {country?.name}
                      </Text>
                    ),
                    value: country?.id,
                  };
                })}
                onChange={(e) => setCountrySelected(e)}
                value={countrySelected}
              />
            }
          />
        }
        bottomSectionStyle={modalStyles.bottomSectionStyle}
        bottomSection={
          countrySelected && (
            <TwoRow
              style={modalStyles.bottomSectionContainerStyle}
              topSection={
                <Text style={modalStyles.questionnaireCountryNameStyle}>
                  {getLocalizedMessage(
                    intl,
                    "modal.viewQuestionnaireCountryName.text",
                    { countryName: getCountryNameById(countrySelected) }
                  )}
                </Text>
              }
              bottomSectionStyle={modalStyles.baseFormEditedWrapperStyle}
              bottomSection={
                <TwoColumn
                  style={modalStyles.baseFormEditedContainerStyle}
                  leftSection={
                    <Text style={modalStyles.viewQuestionnaireTextStyle}>
                      {getLocalizedMessage(
                        intl,
                        "modal.text.questionnaireView"
                      )}
                    </Text>
                  }
                  rightSection={
                    <TwoColumn
                      style={modalStyles.rightSectionBaseFormStyle}
                      leftSection={
                        <Text style={modalStyles.baseFormEditedTextStyle}>
                          {getQuestionnaireEditedByCountryId(countrySelected)
                            ? getLocalizedMessage(
                                intl,
                                "modal.text.baseFormEdited"
                              )
                            : getLocalizedMessage(intl, "modal.text.baseForm")}
                        </Text>
                      }
                      rightSection={
                        <img
                          style={modalStyles.forwardArrow}
                          src={"/icons/arrow_forward_red.svg"}
                          alt="Forward arrow"
                          onClick={() =>
                            getQuestionnaireCodeHandler(countrySelected)
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
          )
        }
      />
    </Modal>
  );
}
